import { useState } from "react";
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from "sweetalert2";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import Pagination from "src/components/pagination";
import usePagination from "src/customHooks/usePagination";
import { showToast } from "src/utils/toast.util";
import { NotificationHealthTable } from "../components/notification-health-table";
import {
  useCountNotificationHealthQuery,
  useGetNotificationHealthQuery,
  useRepairNotificationHealthMutation,
  useRecheckNotificationHealthMutation,
} from "../notification-health.service";
import {
  getNotificationHealthRepairOutcomeDetails,
  NotificationHealth,
} from "../types/notification-health.entity";

const AudienceHealthPage = () => {
  const [activeAction, setActiveAction] = useState<{
    userId: string;
    type: "recheck" | "repair";
  } | null>(null);
  const [recheckNotificationHealth] = useRecheckNotificationHealthMutation();
  const [repairNotificationHealth] = useRepairNotificationHealthMutation();
  const { data, numberOfPages, totalCount, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<NotificationHealth & { id: string }>({
      useGetDataQuery: useGetNotificationHealthQuery,
      useCountData: useCountNotificationHealthQuery,
    });

  const handleRecheck = async (userId: string) => {
    setActiveAction({ userId, type: "recheck" });

    try {
      const result = await recheckNotificationHealth(userId);

      if ("data" in result) {
        const { issueCodes } = result.data;
        showToast(
          "success",
          issueCodes.length
            ? `Recheck completed: ${issueCodes.length} notification-routing exception${issueCodes.length === 1 ? "" : "s"} remain.`
            : "Recheck completed: no current notification-routing exceptions found."
        );
      } else {
        showToast("error", "Could not recheck notification routing. Please try again.");
      }
    } finally {
      setActiveAction(null);
    }
  };

  const handleRepair = async (userId: string) => {
    const confirmation = await Swal.fire({
      title: "Repair notification routing?",
      text: "This will reconcile the user's stored routing and FCM topic subscriptions.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Repair routing",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d97706",
    });

    if (!confirmation.isConfirmed) return;

    setActiveAction({ userId, type: "repair" });

    try {
      const result = await repairNotificationHealth(userId);

      if ("data" in result) {
        const outcome = getNotificationHealthRepairOutcomeDetails(result.data.outcome);
        const toastType =
          result.data.outcome === "REPAIRED" || result.data.outcome === "NO_CHANGES"
            ? "success"
            : result.data.outcome === "MANUAL_ACTION_REQUIRED"
              ? "warrning"
              : "error";
        showToast(toastType, outcome.label);
      } else {
        showToast("error", "Could not repair notification routing. Please try again.");
      }
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <div className="page_container">
      <PageHeader text="Audience Health" />

      {isSuccess ? (
        <>
          <div className="mb-3 flex items-center justify-between text-sm text-slate-600">
            <span>{typeof totalCount === "number" ? `${totalCount} current exception${totalCount === 1 ? "" : "s"}` : "Current exceptions"}</span>
            <span className="text-xs text-slate-400">Notification routing</span>
          </div>
          <NotificationHealthTable
            data={data ?? []}
            onRecheck={handleRecheck}
            onRepair={handleRepair}
            activeAction={activeAction}
          />
          <Pagination numberOfPages={numberOfPages || 1} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700">Could not load audience health. Please try again.</div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default AudienceHealthPage;
